import axios from "axios";
import jwtAuthAxios from "../auth/jwtAuth";
import { setBackendDomain, getBEUrl } from "./management";

/**
 * API client for the ALBACON kiosk test simulator.
 *
 * The /smartkiosk/* endpoints are kiosk-token authenticated (JWT encoding customerId), while the
 * AdminUI holds an admin token. So we first mint a kiosk token via /customer/generateToken (admin
 * authed) and then drive /smartkiosk/* with that token — exactly like a real kiosk.
 *
 * A `mock` mode returns canned responses (no backend calls) so every screen/state can be exercised
 * without the physical machine.
 */

/** Default order payload for customer 62 (editable in the UI). */
export const DEFAULT_SIMULATOR_ORDER = {
  customerId: "62",
  kioskUUID: "b9233549-6909-4756-a724-97db77becf06",
  takeAway: false,
  demo: false,
  locator: null,
  language: "sk",
  payment: { method: "albaconCashMachine" },
  products: [
    {
      sku: "696ce5d44b905951b4d23069",
      qty: 1,
      name: "03. Denné menu Mäsové",
      itemPrice: 0.2,
      totalPrice: 0.2,
      type: "single",
      integrationProductId: 389663,
      customProductId: "#PUBMNU#2",
      vatRate: 5,
      bindings: [
        {
          id: "69090762aa84304a9d659fc4-14323",
          productId: null,
          apiBindingItemId: "696ce5d44b905951b4d2306d",
          customProductId: "#PUBMNU#MOD#157",
          name: "Curry ryža k menu 1ks",
          description: null,
          price: 0,
          imageUrl:
            "https://ik.imagekit.io/ikelp/tr:n-xl/ako1dp/Curry-ryza_2.png",
          isRequired: false,
          autoAdd: false,
          translations: {
            "en-US": { name: "Curry ryža k menu 1ks", description: null },
            "sk-SK": { name: "Curry ryža k menu 1ks", description: null },
          },
          displayPrice: 0,
          mainImage: {
            path: "https://ik.imagekit.io/ikelp/tr:n-xl/ako1dp/Curry-ryza_2.png",
          },
          apiBindingId: "69090762aa84304a9d659fc4",
          quantity: 1,
        },
      ],
    },
  ],
  additionalInfo: {},
  discountCode: null,
  discount: null,
  cartRule: null,
  totalPrice: 0.2,
  totalDiscountedPrice: 0.2,
};

const orderAmount = (order) => {
  const a = Number(order?.totalDiscountedPrice ?? order?.totalPrice);
  return Number.isFinite(a) && a > 0 ? a : Number(order?.totalPrice) || 0;
};

/** Mint a kiosk JWT for the given customer (admin-authed). */
export const generateKioskToken = async (customerId) => {
  setBackendDomain(jwtAuthAxios);
  const { data } = await jwtAuthAxios.post(`/customer/generateToken`, {
    customerId: String(customerId),
  });
  return data?.token;
};

/** Axios instance that talks to /smartkiosk/* as the kiosk (Bearer = kiosk token). */
const kioskClient = (kioskToken) =>
  axios.create({
    baseURL: `${getBEUrl()}/`,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${kioskToken}`,
    },
  });

/* ------------------------------- REAL calls ------------------------------- */

export const realPreflight = async (kioskToken, order) => {
  const client = kioskClient(kioskToken);
  const body = {
    amount: orderAmount(order),
    currency: order?.currency || "EUR",
    kioskUUID: order?.kioskUUID,
    language: order?.language || "sk",
  };
  const { data } = await client.post(`/smartkiosk/orders/cash-preflight`, body);
  return data;
};

export const realCreateOrder = async (kioskToken, order) => {
  const client = kioskClient(kioskToken);
  const { data } = await client.post(`/smartkiosk/orders`, order);
  return data;
};

export const realPollStatus = async (kioskToken, orderId) => {
  const client = kioskClient(kioskToken);
  const { data } = await client.get(
    `/smartkiosk/orders/${orderId}/cash-payment`,
  );
  return data;
};

export const realCancel = async (kioskToken, orderId) => {
  const client = kioskClient(kioskToken);
  const { data } = await client.post(
    `/smartkiosk/orders/${orderId}/cash-payment/cancel`,
  );
  return data;
};

/* ------------------------------- MOCK calls ------------------------------- */
// Canned responses matching the real shapes. `mock` selects which outcome to play.

const MOCK_MESSAGES = {
  block:
    "Hotovosťou teraz nie je možné zaplatiť — automat nevie vydať výdavok. Použite prosím platobnú kartu.",
  limit:
    "Automat vie vydať obmedzený výdavok. Zaplaťte prosím presnou sumou alebo bankovkou najviac 20 EUR.",
  busy: "Automat je momentálne obsadený. Skúste to prosím o chvíľu.",
};

export const mockPreflight = async (order, mock) => {
  const amount = orderAmount(order);
  const decision = mock?.preflightDecision || "allow";
  if (decision === "error") {
    const err = new Error("Could not read machine inventory");
    err.response = {
      status: 502,
      data: { code: 502, status: "Error", reason: "inventory_unavailable" },
    };
    throw err;
  }
  return {
    code: 200,
    status: "OK",
    preflight: {
      decision,
      requiresAcknowledge: decision === "limit",
      maxAcceptedAmount: 20,
      chargedAmount: Math.round((amount / 0.05).valueOf()) * 0.05,
      rounding: 0,
      dispensable: decision === "block" ? 0 : 17.95,
      message: MOCK_MESSAGES[decision] ?? null,
      evaluation: { decision, limited: decision === "limit", mock: true },
    },
  };
};

export const mockCreateOrder = async () => ({
  success: true,
  reason: "PENDING",
  message: "Cash machine payment initiated (mock)",
  cashPayment: { status: "pending" },
  orderId: `mock-${Date.now()}`,
  externalOrderId: `mock-ext-${Date.now()}`,
});

// Poll returns pending a couple of times, then the chosen final status.
export const makeMockPoller = (mock) => {
  let ticks = 0;
  const finalStatus = mock?.finalStatus || "approved";
  const deadlineAt = new Date(Date.now() + 120000).toISOString();
  return async () => {
    ticks += 1;
    const final = ticks >= (mock?.pendingTicks ?? 2);
    return {
      code: 200,
      status: "OK",
      cashPayment: {
        status: final ? finalStatus : "pending",
        final,
        deadlineAt,
        resultCode: final ? 0 : null,
        reasonCode: final && finalStatus !== "approved" ? finalStatus.toUpperCase() : undefined,
      },
    };
  };
};

export const mockCancel = async (mock) => {
  if (mock?.cancelResult === "409") {
    const err = new Error("Payment already completed");
    err.response = {
      status: 409,
      data: { code: 409, status: "Error", reason: "already_approved" },
    };
    throw err;
  }
  return { code: 200, status: "OK", message: "Payment cancelled (mock)" };
};

export { orderAmount };
